import { EmptyChartState } from "@/components/common/EmptyChartState";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import { useChartPalette } from "../utils/chartUtils";
import type { RateCapDataPoint } from "@/types";
import { Box, Typography } from "@mui/material";
import { useAppTheme } from "@/hooks/useAppTheme";
import { getChartTooltipStyle } from "../utils/chartStyles";

interface RateCapPercentageChartProps {
  data: RateCapDataPoint[];
}

/**
 * Displays the distribution of rate cap percentages across all policies
 * with a rate cap endorsement.
 */
export const RateCapPercentageChart = ({ data: rawData }: RateCapPercentageChartProps) => {
  const { theme } = useAppTheme();
  const chartPalette = useChartPalette();

  // Bucket by max_percentage: group data by percentage, sum policy_count
  const buckets: Record<number, number> = {};
  for (const d of rawData) {
    if (d.max_percentage > 0) {
      buckets[d.max_percentage] = (buckets[d.max_percentage] || 0) + d.policy_count;
    }
  }

  const chartData: { name: string; value: number }[] = Object.entries(buckets)
    // max_percentage is stored as a 0–1 decimal, so ×100 for display (0.4 → "40%")
    .map(([pct, count]) => ({
      name: `${parseFloat((Number(pct) * 100).toPrecision(10))}%`,
      value: count,
      _pct: Number(pct),
    }))
    .sort((a, b) => a._pct - b._pct)
    .map(({ name, value }) => ({ name, value }));

  if (chartData.length === 0) {
    return <EmptyChartState />;
  }

  return (
    <Box>
      {/* Custom Legend */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        {chartData.map((item, index) => (
          <Box key={item.name} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                width: 12,
                height: 12,
                backgroundColor: chartPalette[index % chartPalette.length],
                borderRadius: 0.5,
              }}
            />
            <Typography variant="caption">{item.name}</Typography>
          </Box>
        ))}
      </Box>

      <ResponsiveContainer width="100%" height={250}>
        <PieChart margin={{ top: 0, right: 30, bottom: 10, left: 20 }}>
          <Pie data={chartData} dataKey="value" nameKey="name" cx="50%" cy="50%">
            {chartData.map((_, i) => (
              <Cell key={i} fill={chartPalette[i % chartPalette.length]} />
            ))}
          </Pie>
          <Tooltip
            content={({ payload }) => {
              if (!payload || !payload.length) return null;
              const item = payload[0];
              const value = typeof item.value === "number" ? item.value : 0;
              const total = chartData.reduce((sum, d) => sum + d.value, 0);
              const percent = total > 0 ? (value / total) * 100 : 0;
              return (
                <div
                  style={{
                    ...getChartTooltipStyle(theme),
                    padding: "8px",
                  }}
                >
                  <div>
                    <strong>{item.name}</strong>
                  </div>
                  <div>Count: {value}</div>
                  <div>Percentage: {percent.toFixed(1)}%</div>
                </div>
              );
            }}
          />
        </PieChart>
      </ResponsiveContainer>
    </Box>
  );
};
